<template>
  <div>
    <h1 class="mb-3">{{ $t('message.credit.application.employerInfo') }}</h1>
    <p class="mb-4">{{ $t('message.credit.application.askDetails') }}</p>
    <mobialis-form-with-validation
      @onValidate="emitValidation"
      v-av-analytics-form-submit="{ elementText: $t('message.common.continue') }"
    >
      <mobialis-text-input-with-validation
        rules="required"
        :label="employerNameLabel"
        v-model="employerName"
        vid="employerName"
        :helper="isAVRDesktop || isAVREmbed ? $t('message.credit.application.askEmployerNameHelper') : null"
        data-cy="employerName"
      ></mobialis-text-input-with-validation>
      <mobialis-text-input-with-validation
        rules="required|length:10"
        :label="employerPhoneLabel"
        v-model="employerNumber"
        vid="employerNumber"
        mask-type="phoneNumber"
        :helper="isAVRDesktop || isAVREmbed ? $t('message.credit.application.askEmployerPhoneHelper') : null"
        data-cy="employerNumber"
        inputmode="numeric"
        type="tel"
      ></mobialis-text-input-with-validation>
      <mobialis-text-input-with-validation
        rules="required"
        :label="$t('message.credit.application.askStreet#')"
        v-model="streetNumber"
        vid="streetNumber"
        mask-type="naturalNumbers"
        data-cy="streetNumber"
        inputmode="numeric"
      ></mobialis-text-input-with-validation>
      <mobialis-text-input-with-validation
        rules="required"
        :label="$t('message.credit.application.askStreetName')"
        v-model="streetName"
        vid="streetName"
        id="streetName"
        data-cy="streetName"
      ></mobialis-text-input-with-validation>
      <mobialis-text-input-with-validation
        :label="$t('message.credit.application.askSuiteNumber')"
        v-model="suiteNumber"
        vid="suiteNumber"
        id="suiteNumber"
        type="number"
        data-cy="suiteNumber"
        inputmode="numeric"
      ></mobialis-text-input-with-validation>
      <mobialis-text-input-with-validation
        rules="required"
        :label="$t('message.common.city')"
        v-model="city"
        vid="city"
        id="city"
        data-cy="city"
      ></mobialis-text-input-with-validation>
      <mobialis-selection-input-with-validation
        v-if="getThisIsAmerica"
        rules="required"
        :label="$t('message.common.state')"
        v-model="state"
        vid="state"
        :placeHolder="$t('message.common.chooseState')"
        :options="states"
        id="state"
        data-cy="state"
      ></mobialis-selection-input-with-validation>
      <mobialis-selection-input-with-validation
        v-else
        rules="required"
        :label="$t('message.common.province')"
        v-model="province"
        vid="province"
        :placeHolder="$t('message.common.chooseProvince')"
        :options="provinces"
        id="province"
        data-cy="province"
      ></mobialis-selection-input-with-validation>
      <mobialis-text-input-with-validation
        v-if="getThisIsAmerica"
        rules="required|length:5"
        :label="$t('message.common.zipCode')"
        v-model="zipCode"
        vid="zipCode"
        mask-type="zipCode"
        data-cy="zipCode"
        inputmode="numeric"
      ></mobialis-text-input-with-validation>
      <mobialis-text-input-with-validation
        v-else
        rules="required|length:6"
        :label="$t('message.common.postalCode')"
        v-model="postalCode"
        vid="postalCode"
        mask-type="postalCode"
        data-cy="postalCode"
      ></mobialis-text-input-with-validation>
      <mobialis-text-input-with-validation
        rules="required"
        :label="$t('message.credit.application.askCurrentOccupation')"
        v-model="currentOccupation"
        vid="currentOccupation"
        id="currentOccupation"
        :helper="isAVRDesktop || isAVREmbed ? $t('message.credit.application.askCurrentOccupationHelper') : null"
        data-cy="currentOccupation"
      ></mobialis-text-input-with-validation>
      <mobialis-text-input-with-validation
        rules="required|max_value:99999999"
        :label="$t('message.credit.application.askGrossIncome')"
        v-model="grossIncome"
        vid="grossIncome"
        id="grossIncome"
        mask-type="money"
        :helper="isAVRDesktop || isAVREmbed ? $t('message.credit.application.askGrossIncomeHelper') : null"
        :readonly="hasGrossIncome"
        data-cy="grossIncome"
      ></mobialis-text-input-with-validation>
      <mobialis-text-input-with-validation
        rules="required|min_value:1|max_value:99"
        :label="$t('message.credit.application.askYearsCurrentEmployer')"
        v-model="yearsWorked"
        vid="yearsWorked"
        :options="years"
        mask-type="naturalNumbers"
        data-cy="yearsWorked"
        inputmode="numeric"
      ></mobialis-text-input-with-validation>
      <mobialis-button type="submit" class="my-4" data-cy="submitEmployerForm">{{
        $t('message.common.continue')
      }}</mobialis-button>
    </mobialis-form-with-validation>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import log from '@/services/log';
import { provinceMixin, stateMixin } from '@/mixins';

export default {
  name: 'EmployerForm',
  mixins: [provinceMixin, stateMixin],
  data() {
    return {
      years: [],
      employerName: '',
      employerNumber: '',
      streetNumber: '',
      streetName: '',
      suiteNumber: '',
      city: '',
      province: '',
      state: '',
      postalCode: '',
      zipCode: '',
      currentOccupation: '',
      grossIncome: '',
      yearsWorked: '',
      hasGrossIncome: false,
    };
  },
  computed: {
    ...mapGetters('business', ['getThisIsAmerica']),
    employerNameLabel() {
      return this.isAVRDesktop || this.isAVREmbed
        ? this.$t('message.credit.application.askEmployerNameDesktop')
        : this.$t('message.credit.application.askEmployerNameMobile');
    },
    employerPhoneLabel() {
      return this.isAVRDesktop || this.isAVREmbed
        ? this.$t('message.credit.application.askEmployerPhoneDesktop')
        : this.$t('message.credit.application.askEmployerPhoneMobile');
    },
  },
  mounted() {
    this.employerName = this.$store.state.user.financeApplication.employerName;
    this.employerNumber = this.$store.state.user.financeApplication.employerNumber;
    this.streetNumber = this.$store.state.user.financeApplication.streetNumber;
    this.streetName = this.$store.state.user.financeApplication.streetName;
    this.suiteNumber = this.$store.state.user.financeApplication.suiteNumber;
    this.city = this.$store.state.user.financeApplication.city;
    this.province = this.$store.state.user.financeApplication.province;
    this.state = this.$store.state.user.financeApplication.province;
    this.postalCode = this.$store.state.user.financeApplication.postalCode;
    this.zipCode = this.$store.state.user.financeApplication.postalCode;
    this.currentOccupation = this.$store.state.user.financeApplication.currentOccupation;
    this.grossIncome = this.$store.state.user.financeDetails.grossIncome;
    this.yearsWorked = this.$store.state.user.financeApplication.yearsWorked;
    this.hasGrossIncome = !!this.$store.state.user.financeDetails.grossIncome;
  },
  methods: {
    async emitValidation(isValid) {
      if (!isValid) {
        return;
      }
      try {
        this.$store.commit('user/SET_EMPLOYER_NAME', this.employerName);
        this.$store.commit('user/SET_EMPLOYER_NUMBER', this.employerNumber);
        this.$store.commit('user/SET_EMPLOYER_STREET_NUMBER', this.streetNumber);
        this.$store.commit('user/SET_EMPLOYER_STREET_NAME', this.streetName);
        this.$store.commit('user/SET_EMPLOYER_SUIT_NUMBER', this.suiteNumber);
        this.$store.commit('user/SET_EMPLOYER_CITY', this.city);
        this.$store.commit(
          'user/SET_EMPLOYER_PROVINCE_CODE',
          this.getThisIsAmerica ? this.getStateCode(this.state) : this.getProvinceCode(this.province)
        );
        if (this.getThisIsAmerica) {
          this.$store.commit('user/SET_EMPLOYER_STATE', this.state);
          this.$store.commit('user/SET_EMPLOYER_ZIPCODE', this.zipCode);
        } else {
          this.$store.commit('user/SET_EMPLOYER_PROVINCE', this.province);
          this.$store.commit('user/SET_EMPLOYER_POSTALCODE', this.postalCode);
        }
        this.$store.commit('user/SET_CURRENT_OCCUPATION', this.currentOccupation);
        this.$store.commit('user/SET_GROSS_INCOME', this.grossIncome);
        this.$store.commit('user/SET_YEARS_WORKED', this.yearsWorked);
        this.$router.push('mortgage-form');
      } catch (err) {
        this.$store.commit('SET_LOADING', false);
        log('Error in Finance Application Employer Form', err);
        this.$router.push({ name: `credit-in-error-${this.baseRoute}` });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
h1 {
  font-size: 28px;
  font-weight: bold;
  margin-top: 29px; // Custom margin specified in Abstract
}
</style>
